import { useRef, useState } from "react";

/**
 * Click-to-place focal point picker for a product's hero image.
 * Left panel shows the FULL, uncropped image (object-contain) so click
 * coordinates map 1:1 to image percentage — clicking sets a pin at that
 * exact spot. Right panel is a live portrait-aspect preview showing exactly
 * how that focal point will crop on a real mobile screen (object-cover).
 *
 * The container is a fixed 16:9 box, but the image itself is only ever
 * `object-contain`-fit inside it — for portrait/square source images (e.g.
 * a video-frame still) that leaves large letterboxed bars on either side.
 * Click math and the pin position must both be computed against the
 * actual rendered IMAGE rect inside the container (not the container's own
 * bounds), otherwise clicks in/near the letterbox map to nonsense percentages
 * and the pin never lines up with where you actually clicked.
 *
 * `value` / `onChange` use the CSS object-position string format, e.g. "70% 30%".
 */
export const FocalPointPicker = ({
  imageSrc,
  value,
  onChange,
  instructionText = "Click the image to set the mobile focal point",
  previewLabel = "Mobile preview",
  previewAspectClass = "aspect-[9/16]",
  previewWidthClass = "w-28",
  testIdPrefix = "admin-focal-point",
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState(null); // { w, h }
  const [xStr, yStr] = (value || "50% 50%").split(" ");
  const x = parseFloat(xStr) || 50;
  const y = parseFloat(yStr) || 50;

  // The rect the image actually occupies inside the container, given
  // object-contain scaling — everything outside this rect is letterbox.
  const getImageRect = () => {
    const container = containerRef.current;
    if (!container || !naturalSize) return null;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const scale = Math.min(cw / naturalSize.w, ch / naturalSize.h);
    const renderedW = naturalSize.w * scale;
    const renderedH = naturalSize.h * scale;
    return {
      left: (cw - renderedW) / 2,
      top: (ch - renderedH) / 2,
      width: renderedW,
      height: renderedH,
    };
  };

  const handleClick = (e) => {
    const imgRect = getImageRect();
    if (!imgRect) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const clickX = e.clientX - containerRect.left - imgRect.left;
    const clickY = e.clientY - containerRect.top - imgRect.top;
    const newX = Math.round(Math.max(0, Math.min(100, (clickX / imgRect.width) * 100)));
    const newY = Math.round(Math.max(0, Math.min(100, (clickY / imgRect.height) * 100)));
    onChange(`${newX}% ${newY}%`);
  };

  // Convert the stored image-relative x/y % back into container-relative
  // percentages so the pin renders on top of the actual image, not floating
  // inside a letterbox bar.
  const imgRect = getImageRect();
  const container = containerRef.current;
  let pinLeftPct = x;
  let pinTopPct = y;
  if (imgRect && container) {
    pinLeftPct = ((imgRect.left + (x / 100) * imgRect.width) / container.clientWidth) * 100;
    pinTopPct = ((imgRect.top + (y / 100) * imgRect.height) / container.clientHeight) * 100;
  }

  return (
    <div className="flex flex-wrap items-start gap-4">
      <div className="min-w-[220px] flex-1 max-w-md">
        <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-tertiary)] mb-2">
          {instructionText}
        </p>
        <div
          ref={containerRef}
          data-testid={`${testIdPrefix}-picker`}
          onClick={handleClick}
          className="relative w-full aspect-video bg-black overflow-hidden border border-[var(--border-default)] cursor-crosshair select-none"
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt=""
            onLoad={(e) => setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          />
          <div
            data-testid={`${testIdPrefix}-pin`}
            className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--brand-primary)] shadow-[0_0_0_2px_rgba(0,0,0,0.7)] pointer-events-none"
            style={{ left: `${pinLeftPct}%`, top: `${pinTopPct}%` }}
          />
        </div>
      </div>
      <div className="w-28 shrink-0">
        <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-tertiary)] mb-2">{previewLabel}</p>
        <div data-testid={`${testIdPrefix}-preview`} className={`relative ${previewWidthClass} ${previewAspectClass} overflow-hidden border border-[var(--border-default)]`}>
          <img
            src={imageSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: `${x}% ${y}%` }}
          />
        </div>
      </div>
    </div>
  );
};
